/**
 * Rating validation, latest/previous rated tasting selection, and trend.
 *
 * See specs/001-wine-cellar-tracker/spec.md FR-013, FR-017 and the
 * "Ratings and unrated wines" edge case. Extended in User Story 5
 * (tasks T085-T086) for the full rating-history view.
 */
import { compareMonths, type MonthValue } from './months';

export type Rating = 1 | 1.5 | 2 | 2.5 | 3 | 3.5 | 4 | 4.5 | 5;

/** A tasting has a rating between 1 and 5 in half-point steps. */
export function isValidRating(value: number): value is Rating {
  return value >= 1 && value <= 5 && Number.isInteger(value * 2);
}

export type StarFill = 'full' | 'half' | 'empty';

/**
 * The five stars drawn next to a rating (owner feedback, 2026-09-28): whole stars for whole
 * points, half a star for a half point.
 */
export function starFills(rating: number): StarFill[] {
  return [1, 2, 3, 4, 5].map((star) =>
    rating >= star ? 'full' : rating >= star - 0.5 ? 'half' : 'empty',
  );
}

export interface RatedTasting {
  tastedOn: MonthValue;
  /** Increasing per-wine counter giving entry order; breaks ties within the same month. */
  sequence: number;
  rating?: number;
}

function compareChronological(a: RatedTasting, b: RatedTasting): number {
  const byMonth = compareMonths(a.tastedOn, b.tastedOn);
  return byMonth !== 0 ? byMonth : a.sequence - b.sequence;
}

/**
 * Every tasting by month, then in the order entered within a month (User Story 5, Acceptance 1).
 * Returns a new array; extra fields of each tasting are kept.
 */
export function chronologicalTastings<T extends RatedTasting>(tastings: readonly T[]): T[] {
  return [...tastings].sort(compareChronological);
}

/** The wine's "Historique" (owner, 2026-10-11): the most recent tasting first. */
export function newestFirst<T extends RatedTasting>(tastings: readonly T[]): T[] {
  return chronologicalTastings(tastings).reverse();
}

/** The rated tastings only, in chronological order: the points of the rating chart. */
export function ratedHistory<T extends RatedTasting>(tastings: readonly T[]): T[] {
  return chronologicalTastings(tastings.filter((t) => t.rating !== undefined));
}

const ratedOnly = ratedHistory;

/** The most recent tasting that has a rating (never an unrated one), or undefined if none. */
export function latestRatedTasting(tastings: readonly RatedTasting[]): RatedTasting | undefined {
  const rated = ratedOnly(tastings);
  return rated[rated.length - 1];
}

/** The rated tasting immediately before the latest one, or undefined with fewer than two. */
export function previousRatedTasting(tastings: readonly RatedTasting[]): RatedTasting | undefined {
  const rated = ratedOnly(tastings);
  return rated[rated.length - 2];
}

export type RatingTrend = 'up' | 'down' | 'same' | 'none';

/** 'none' when there is no previous rated tasting to compare against (never a misleading trend). */
export function ratingTrend(latest: number | undefined, previous: number | undefined): RatingTrend {
  if (latest === undefined || previous === undefined) {
    return 'none';
  }
  if (latest > previous) return 'up';
  if (latest < previous) return 'down';
  return 'same';
}

/** The card's ▲/▼/= mark: latest rated tasting against the previous rated one (Story 5, T092). */
export function tastingsTrend(tastings: readonly RatedTasting[]): RatingTrend {
  const rated = ratedHistory(tastings);
  return ratingTrend(rated[rated.length - 1]?.rating, rated[rated.length - 2]?.rating);
}
